import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react'
import { AuthContext, type AuthUser } from './AuthContext'

function toSafeUser(value: unknown): AuthUser | null {
  if (!value || typeof value !== 'object') return null
  const candidate = value as Record<string, unknown>
  if (typeof candidate.id !== 'string') return null

  return {
    id: candidate.id,
    email: typeof candidate.email === 'string' ? candidate.email : null,
    display_name:
      typeof candidate.display_name === 'string' ? candidate.display_name : null,
    admin: candidate.admin === true || candidate.admin === 1,
    moderator: candidate.moderator === true || candidate.moderator === 1,
    providers: Array.isArray(candidate.providers)
      ? candidate.providers.filter(
          (provider): provider is string => typeof provider === 'string',
        )
      : [],
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null)
  const [loading, setLoading] = useState(true)

  const refresh = useCallback(async () => {
    setLoading(true)
    try {
      const response = await fetch('/api/auth/session', {
        credentials: 'include',
        headers: { Accept: 'application/json' },
      })
      if (!response.ok) {
        setUser(null)
        return
      }
      const body: unknown = await response.json()
      const sessionUser =
        body && typeof body === 'object' && 'user' in body
          ? (body as { user?: unknown }).user
          : body
      setUser(toSafeUser(sessionUser))
    } catch {
      setUser(null)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    void refresh()
  }, [refresh])

  const logout = useCallback(async () => {
    const response = await fetch('/api/auth/logout', {
      method: 'POST',
      credentials: 'include',
      headers: { Accept: 'application/json' },
    })
    if (!response.ok) throw new Error('Sign out failed')
    setUser(null)
  }, [])

  const value = useMemo(
    () => ({ user, loading, refresh, logout }),
    [loading, logout, refresh, user],
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}
